
import { defineComponent, onMounted, ref, computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { required } from '@vuelidate/validators';

import useVuelidate from '@vuelidate/core';

import api from '@/api';
import { ApiTermsAndConditions } from '@/api/types/terms-and-conditions';

export default defineComponent({
    props: {
        isMobile: {
            type: Boolean,
            required: true,
        },
        isAccepted: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['close'],
    setup() {
        const isTermsAndConditionsModalOpen = computed(() => store.getters['ui/isTermsAndConditionsModalOpen']);
        const { t } = useI18n({ useScope: 'global' });
        const store = useStore();
        const gtcData = ref<ApiTermsAndConditions>();
        const dtpData = ref<ApiTermsAndConditions>();
        const gtcAccepted = ref<boolean>(false);
        const dtpAccepted = ref<boolean>(false);
        const gtcError = ref<boolean>(false);
        const dtpError = ref<boolean>(false);
        const refGtcCheckbox = ref<Ref>(null);
        const refDtpCheckbox = ref<Ref>(null);

        const validationRules = {
            gtcAccepted: {
                required,
                isTrue: (val) => val === true,
            },
            dtpAccepted: {
                required,
                isTrue: (val) => val === true,
            },
        };

        const v$ = useVuelidate(validationRules, {
            gtcAccepted,
            dtpAccepted,
        });

        const scrollToCheckbox = (ref: Ref) => {
            ref.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };

        onMounted(async () => {
            try {
                const token = window.localStorage.getItem('authcredentials');
                const responseGtc = await api.terms.getGtc(token);
                const responseDtp = await api.terms.getDtp(token);
                gtcData.value = responseGtc.data;
                dtpData.value = responseDtp.data;
            } catch (error) {
                new Error(error);
            }
        });

        const handleGtcCheck = () => {
            gtcAccepted.value = !gtcAccepted.value;
            gtcError.value = false;
        };

        const handleDtpCheck = () => {
            dtpAccepted.value = !dtpAccepted.value;
            dtpError.value = false;
        };

        const saveChanges = async () => {
            v$.value.$validate();

            if (v$.value.gtcAccepted.$error) {
                gtcError.value = true;
            }

            if (v$.value.dtpAccepted.$error) {
                dtpError.value = true;
            }

            if (v$.value.gtcAccepted.$error) {
                scrollToCheckbox(refGtcCheckbox);
                gtcError.value = true;
                return;
            }

            if (v$.value.dtpAccepted.$error) {
                scrollToCheckbox(refDtpCheckbox);
                dtpError.value = true;
                return;
            }

            if (v$.value.$errors.length === 0) {
                try {
                    const token = window.localStorage.getItem('authcredentials');
                    await api.terms.confirmTermsAndConditions(token);
                    await store.dispatch('auth/account');
                } catch (error) {
                    new Error(error);
                }
            }
        };
        return {
            t,
            gtcData,
            dtpData,
            gtcError,
            dtpError,
            gtcAccepted,
            dtpAccepted,
            refGtcCheckbox,
            refDtpCheckbox,
            isTermsAndConditionsModalOpen,
            saveChanges,
            handleGtcCheck,
            handleDtpCheck,
        };
    },
});
